import React, { useState, useRef, useEffect, useCallback } from "react";
const MIN_BRIGHTNESS = 1; // Minimum average brightness required

const EnhancedKYCCamera = () => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const folderIconRef = useRef(null);
    const containerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [stream, setStream] = useState(null);
    const [status, setStatus] = useState("Click 'Start Camera' to capture photo");
    const [isAnimating, setIsAnimating] = useState(false);
    const [storedImagesCount, setStoredImagesCount] = useState(0);
    const [uploadProgress, setUploadProgress] = useState(null);
    const Id = localStorage.getItem("userId")



    // Increased oval dimensions for a bigger overlay
    const OVAL_RADIUS_X = 250;
    const OVAL_RADIUS_Y = 300;

    // Clean up on unmount
    useEffect(() => {
        return () => stopCamera();  // eslint-disable-next-line
    }, []);

    // Attach stream to video element when stream changes
    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(console.error);
        }
    }, [stream]);

    // Continuously check lighting conditions while camera is active
    useEffect(() => {
        let intervalId;
        if (stream) {
            intervalId = setInterval(() => {
                const brightness = checkLighting();
                if (brightness < MIN_BRIGHTNESS) {
                    setStatus("Lighting is too dim. Please move to a brighter area.");
                } else {
                    setStatus("Camera is active. Position your face within the oval.");
                }
            }, 1000); // Check lighting every second
        }
        return () => clearInterval(intervalId);
    }, [stream]);

    // Start camera stream
    const startCamera = async () => {
        try {
            setIsLoading(true);
            setStatus("Requesting camera access...");

            const constraints = {
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: "user",
                },
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);

            // Wait for video metadata to load before playing
            await new Promise((resolve) => {
                videoRef.current.onloadedmetadata = () => resolve();
            });
            videoRef.current.play();

            // Set canvas dimensions to match video
            if (canvasRef.current) {
                canvasRef.current.width = videoRef.current.videoWidth;
                canvasRef.current.height = videoRef.current.videoHeight;
                const ctx = canvasRef.current.getContext("2d");
                drawOvalGuide(ctx, canvasRef.current);
            }

            setStatus("Camera is active. Position your face within the oval.");
        } catch (err) {
            console.error("Error accessing camera:", err);
            setStatus("Error accessing camera: " + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Stop camera stream
    const stopCamera = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
        }
        setStatus("Camera stopped.");
        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext("2d");
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
    }, [stream]);

    // Draw the oval guide
    const drawOvalGuide = (ctx, canvas) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        ctx.strokeStyle = "#3498db";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, OVAL_RADIUS_X, OVAL_RADIUS_Y, 0, 0, 2 * Math.PI);
        ctx.stroke();
    };

    // Check the average brightness of a 50x50 sample from the video
    const checkLighting = () => {
        if (!videoRef.current) return 0;
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = 50;
        tempCanvas.height = 50;
        const ctx = tempCanvas.getContext("2d");
        ctx.drawImage(videoRef.current, 0, 0, 50, 50);
        const imageData = ctx.getImageData(0, 0, 50, 50);
        const data = imageData.data;
        let totalBrightness = 0;
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i],
                g = data[i + 1],
                b = data[i + 2];
            // Calculate perceived brightness
            const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
            totalBrightness += brightness;
        }
        const avgBrightness = totalBrightness / (data.length / 4);
        return avgBrightness;
    };

    // Upload image to database
    // Replace the existing uploadImageToDatabase function with this
    const uploadImageToDatabase = async (imageBlob, imageType = "captured") => {
        try {
            setUploadProgress(0);

            // Create form data
            const formData = new FormData();
            formData.append("file", imageBlob, "capture.jpg");
            formData.append("image_type", imageType);
            formData.append("user_id", Id);

            // Use fetch with AbortController for cancelation support
            const controller = new AbortController();
            const signal = controller.signal;

            const response = await fetch(`${process.env.REACT_APP_API_KEY}/images/upload_image_to_DB`, {
                method: 'POST',
                body: formData,
                signal: signal,
            });

            // Set progress to 100% when complete
            setUploadProgress(100);

            if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
            }

            const result = await response.json();
            setUploadProgress(null);
            return result;
        } catch (error) {
            console.error("Error uploading image:", error);
            setUploadProgress(null);
            throw error;
        }
    };

    // Capture image with animation
    const captureImage = async () => {

        if (!videoRef.current || !stream) {
            setStatus("Please start the camera first");
            return;
        }

        if (isAnimating) {
            return; // Prevent multiple captures during animation
        }

        const brightness = checkLighting();
        if (brightness < MIN_BRIGHTNESS) {
            setStatus("Lighting is too dim. Please move to a brighter area.");
            return;
        }

        try {
            const captureCanvas = document.createElement("canvas");
            captureCanvas.width = videoRef.current.videoWidth;
            captureCanvas.height = videoRef.current.videoHeight;
            const ctx = captureCanvas.getContext("2d");

            // Capture the current video frame WITHOUT drawing the oval overlay
            ctx.drawImage(videoRef.current, 0, 0, captureCanvas.width, captureCanvas.height);

            // Get the image as a blob for uploading
            const imageBlob = await new Promise(resolve => {
                captureCanvas.toBlob(blob => resolve(blob), "image/jpeg", 0.9);
            });

            // Get data URL for animation
            const imageDataURL = captureCanvas.toDataURL("image/jpeg");

            // Get folder and container positions for animation calculations
            const folderRect = folderIconRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            // Calculate animation end position relative to viewport
            const endX = folderRect.left + folderRect.width / 2 - 260; // Center of the folder minus half of final width
            const endY = folderRect.top + folderRect.height / 2 - 256; // Center of the folder minus half of final height

            // Create a temporary image element for animation
            const imgElement = document.createElement("img");
            imgElement.src = imageDataURL;

            // Start the animation
            setIsAnimating(true);
            setStatus("Processing image...");

            // Create floating image div that will be positioned absolutely in the viewport
            const floatingImg = document.createElement("div");
            floatingImg.className = "floating-image";
            floatingImg.style.backgroundImage = `url(${imageDataURL})`;

            // Set initial position to match the camera view
            floatingImg.style.top = `${containerRect.top}px`;
            floatingImg.style.left = `${containerRect.left}px`;
            floatingImg.style.width = `${containerRect.width}px`;
            floatingImg.style.height = `${containerRect.height}px`;

            // Set custom properties for animation end position
            document.documentElement.style.setProperty('--folder-x', `${endX}px`);
            document.documentElement.style.setProperty('--folder-y', `${endY}px`);

            // Add to body (not the container) for absolute positioning relative to viewport
            document.body.appendChild(floatingImg);

            // Upload the image to database while animation is running
            try {
                const uploadResult = await uploadImageToDatabase(imageBlob);
                console.log("Upload successful:", uploadResult);

                // After animation completes
                setTimeout(() => {
                    // Remove the floating image
                    floatingImg.remove();
                    setIsAnimating(false);
                    setStoredImagesCount(prev => prev + 1);
                    setStatus(`Image captured and stored (${storedImagesCount + 1} photos)`);
                }, 3000); // Animation duration increased to 3 seconds (slower)

            } catch (uploadError) {
                // If upload fails, still complete the animation but show error
                console.error("Upload failed:", uploadError);
                setTimeout(() => {
                    floatingImg.remove();
                    setIsAnimating(false);
                    setStatus(`Error storing image: ${uploadError.message}`);
                }, 3000);
            }

        } catch (err) {
            console.error("Error capturing image:", err);
            setStatus("Failed to capture image");
            setIsAnimating(false);
        }
    };

    // Improved Folder Icon SVG
    const FolderIcon = () => (
        <svg width="60" height="52" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="folderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#63b3ed" />
                    <stop offset="100%" stopColor="#3182ce" />
                </linearGradient>
            </defs>
            <path d="M2 6C2 4.89543 2.89543 4 4 4H9L11 6H20C21.1046 6 22 6.89543 22 8V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z"
                fill="url(#folderGradient)" stroke="#2c5282" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 6H9L11 8H20C21.1046 8 22 8.89543 22 10V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6C2 5.44772 2.44772 5 3 5H4Z"
                fill="#90cdf4" stroke="#2c5282" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    return (
        <div style={{ maxWidth: "800px", margin: "90px auto", padding: "1rem", position: "relative" }}>
            <h2 style={{ textAlign: "center", marginBottom: "1.5rem" }}>Take Photo</h2>

            {/* Folder Icon in top right corner - removed fixed positioning */}
            <div
                ref={folderIconRef}
                style={{
                    position: "absolute",
                    top: "-10px",
                    right: "-430px",
                    zIndex: 1000,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.3)"
                }}
            >
                <FolderIcon />
                {storedImagesCount > 0 && (
                    <div style={{
                        position: "absolute",
                        top: "-5px",
                        right: "-5px",
                        backgroundColor: "#e74c3c",
                        color: "white",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                    }}>
                        {storedImagesCount}
                    </div>
                )}
            </div>

            {status && (
                <div
                    style={{
                        padding: "0.75rem 1rem",
                        marginBottom: "1rem",
                        borderRadius: "4px",
                        backgroundColor: "#3498db20",
                        color: "#3498db",
                        border: "1px solid #3498db",
                        textAlign: "center",
                    }}
                >
                    {status}
                </div>
            )}

            {/* Upload Progress Bar */}
            {uploadProgress !== null && (
                <div style={{ marginBottom: "1rem" }}>
                    <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                        Uploading image... {uploadProgress}%
                    </div>
                    <div style={{
                        height: "6px",
                        backgroundColor: "#e2e8f0",
                        borderRadius: "3px",
                        overflow: "hidden"
                    }}>
                        <div
                            style={{
                                height: "100%",
                                width: `${uploadProgress}%`,
                                backgroundColor: "#3498db",
                                transition: "width 0.3s ease-in-out"
                            }}
                        />
                    </div>
                </div>
            )}

            <div
                ref={containerRef}
                className="camera-container"
                style={{ position: "relative", marginBottom: "1rem" }}
            >
                <div style={{ position: "relative", border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden" }}>
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ width: "100%", height: "auto", backgroundColor: "#f8f9fa", display: "block" }}
                    />
                    <canvas
                        ref={canvasRef}
                        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}
                    />

                    {isLoading && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: "rgba(0,0,0,0.5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                flexDirection: "column",
                            }}
                        >
                            <div style={{ marginBottom: "10px" }}>Starting camera...</div>
                            <div
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    border: "4px solid rgba(255,255,255,0.3)",
                                    borderTop: "4px solid white",
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite",
                                }}
                            ></div>
                        </div>
                    )}
                </div>
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "1rem",
                    flexWrap: "wrap",
                    marginTop: "1rem",
                }}
            >
                <button
                    onClick={startCamera}
                    disabled={isLoading || stream}
                    style={{
                        padding: "0.75rem 1.5rem",
                        fontSize: "1rem",
                        backgroundColor: "#2980b9",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: stream ? "not-allowed" : "pointer",
                        opacity: isLoading || stream ? 0.7 : 1,
                    }}
                >
                    Start Camera
                </button>
                <button
                    onClick={stopCamera}
                    disabled={isLoading || !stream}
                    style={{
                        padding: "0.75rem 1.5rem",
                        fontSize: "1rem",
                        backgroundColor: "#7f8c8d",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: !stream ? "not-allowed" : "pointer",
                        opacity: isLoading || !stream ? 0.7 : 1,
                    }}
                >
                    Stop Camera
                </button>
                <button
                    onClick={captureImage}
                    disabled={isLoading || !stream || isAnimating || uploadProgress !== null}
                    style={{
                        padding: "0.75rem 1.5rem",
                        fontSize: "1rem",
                        backgroundColor: "#27ae60",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: !stream || isAnimating || uploadProgress !== null ? "not-allowed" : "pointer",
                        opacity: isLoading || !stream || isAnimating || uploadProgress !== null ? 0.7 : 1,
                    }}
                >
                    Capture Image
                </button>
            </div>

            {/* Animation keyframes and styles - Need to update animation to account for scrolling */}
            <style>
                {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .floating-image {
            position: fixed;
            background-size: cover;
            background-position: center;
            border: 3px solid white;
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            z-index: 9999;
            animation: moveToFolder 3s ease-out forwards;
            pointer-events: none;
          }
          
          @keyframes moveToFolder {
            0% {
              transform: scale(1);
              opacity: 0.9;
            }
            20% {
              transform: scale(0.9);
              opacity: 0.9;
            }
            100% {
              transform: translate(calc(var(--folder-x) - 60%), calc(var(--folder-y) - 50%)) scale(0.15);
              opacity: 0;
            }
          }
        `}
            </style>
        </div>
    );
};

export default EnhancedKYCCamera;